import { Badge, Box, Button, HStack, Image, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

const BADGE_STYLES = {
    owned: { bg: "green.100", borderColor: "green.200", color: "green.700" },
    buy: { bg: "purple.100", borderColor: "purple.200", color: "purple.700" },
    soon: { bg: "gray.100", borderColor: "gray.200", color: "gray.600" },
};

const BADGE_LABEL_KEY = { owned: "active", buy: "available", soon: "soon" };

const TILE = 120;
const RADIUS = 22;

// Straight-line bottom clip, not the tile's rounded corners -- a rounded clip slices through a hand
// hanging over the corner (see cyberjob.ai's own tiles for the intended look).
const tileClip = (size) => {
    const bleed = Math.round(size * 0.4);
    return `inset(${-bleed}px ${-bleed}px 0 ${-bleed}px)`;
};

export const AgentAvatar = ({ image, tile, dimmed, size = TILE }) => (
    <Box
        position="relative"
        w={`${size}px`}
        h={`${size}px`}
        flexShrink={0}
        clipPath={tileClip(size)}
        filter={dimmed ? "grayscale(0.85)" : undefined}
        opacity={dimmed ? 0.7 : 1}
    >
        <Box position="absolute" inset={0} borderRadius={`${Math.round(RADIUS * size / TILE)}px`} style={{ background: tile }} />
        <Image
            src={image}
            alt=""
            position="absolute"
            left="50%"
            bottom={0}
            h={`${Math.round(size * 1.19)}px`}
            maxW="none"
            transform="translateX(-50%)"
            draggable={false}
        />
    </Box>
);

/**
 * state: "owned" (user has this agent, opens its dashboard), "buy" (product line the user
 * doesn't have yet) or "soon" (not released, no action).
 */
const AgentCatalogCard = ({ name, description, image, tile, state, onAction }) => {
    const { t } = useTranslation("models");
    const isSoon = state === "soon";
    const badge = BADGE_STYLES[state];

    return (
        <VStack
            align="stretch"
            justify="space-between"
            gap={5}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius={16}
            p={5}
            pt={6}
            transition="box-shadow 0.2s, border-color 0.2s, transform 0.2s"
            animation="fadeSlideIn 0.25s ease-out"
            _hover={isSoon ? undefined : { boxShadow: "0 8px 28px rgba(23,21,31,0.10)", borderColor: "purple.200", transform: "translateY(-2px)" }}
        >
            <VStack align="stretch" gap={4}>
                <HStack justify="space-between" align="flex-start">
                    <AgentAvatar image={image} tile={tile} dimmed={isSoon} />
                    <Badge
                        {...badge}
                        borderWidth={1}
                        variant="subtle"
                        fontSize="xs"
                        px={3}
                        py={0.5}
                        borderRadius={6}
                    >
                        {t(`agentsCatalog.badge.${BADGE_LABEL_KEY[state]}`)}
                    </Badge>
                </HStack>
                <VStack align="stretch" gap={1.5}>
                    <Text fontWeight={700} fontSize="lg" color={isSoon ? "gray.600" : "gray.900"} lineHeight={1.25}>
                        {name}
                    </Text>
                    <Text fontSize="sm" color="gray.500" lineHeight={1.6}>
                        {description}
                    </Text>
                </VStack>
            </VStack>
            {!isSoon && (
                <Button variant={state === "buy" ? "primary" : "outlined"} alignSelf="flex-start" onClick={onAction}>
                    {t(state === "buy" ? "agentsCatalog.action.buy" : "agentsCatalog.action.open")}
                </Button>
            )}
        </VStack>
    );
};

export default AgentCatalogCard;
